// src/pages/UploadSign.tsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

export default function UploadSign() {
  const navigate = useNavigate();
  const [nextSignerEmail, setNextSignerEmail] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleSubmit = async () => {
    if (!nextSignerEmail || !selectedFile) {
      alert("Please enter the next signer email and select a PDF file.");
      return;
    }

    setLoading(true);

    try {
      // Step 1: Read PDF as base64
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(selectedFile);
      });

      const base64Data = await base64Promise;
      const base64Content = base64Data.split(",")[1]; // Remove data:application/pdf;base64,

      // Step 2: Upload to /api/upload
      const uploadRes = await axios.post("http://localhost:5000/api/upload", {
        fileName: selectedFile.name,
        fileContentBase64: base64Content,
      });

      if (!uploadRes.data.success) {
        throw new Error("Upload failed");
      }

      const publicPdfUrl = uploadRes.data.publicPdfUrl;

      // Step 3: Save document to /api/save-document
      const saveRes = await axios.post("http://localhost:5000/api/save-document", {
        Title: selectedFile.name,
        publicPdfUrl,
        NextSignerEmail: nextSignerEmail,
      });

      if (saveRes.data.success) {
        alert("Document uploaded successfully! Email sent to next signer.");
        setNextSignerEmail("");
        setSelectedFile(null);
      } else {
        throw new Error("Save failed");
      }
    } catch (err: any) {
      console.error(err);
      alert("Error: " + (err.response?.data?.error || "Failed to upload and send document."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", fontFamily: "'Segoe UI', sans-serif" }}>
      {/* BLUE HEADER WITH NAV BUTTONS */}
<header style={{
        background: "linear-gradient(90deg, #003f7d, #005eb8)",
        color: "white",
        padding: "60px 40px 100px",
        textAlign: "center"
      }}>
        <h1 style={{ fontSize: "2.5rem", margin: "0 0 12px 0", fontWeight: "700" }}>
          Digital Document Signing System
        </h1>
        <p style={{ fontSize: "1.3rem", margin: "0 0 40px 0", opacity: 0.9, letterSpacing: "1px" }}>
          FAST • SECURE • PAPERLESS
        </p>

        {/* NAVIGATION PILLS */}
        <div style={{ display: "flex", justifyContent: "center", gap: "16px", flexWrap: "wrap" }}>
          <button  onClick={() => navigate("/Home")}style={{ padding: "10px 24px", borderRadius: "30px", border: "none", background: "rgba(255,255,255,0.25)", color: "white", fontWeight: "500", cursor: "pointer" }}>Dashboard</button>
          <button onClick={() => navigate("/createsign")} style={{ padding: "10px 24px", borderRadius: "30px", border: "none", background: "rgba(255,255,255,0.25)", color: "white", fontWeight: "500", cursor: "pointer" }}>Create Signature</button>
          <button onClick={() => navigate("/viewsign")} style={{ padding: "10px 24px", borderRadius: "30px", border: "none", background: "rgba(255,255,255,0.25)", color: "white", fontWeight: "500", cursor: "pointer" }}>View Signature</button>
          <button style={{ padding: "12px 28px", borderRadius: "30px", border: "none", background: "white", color: "#003f7d", fontWeight: "600", cursor: "pointer", boxShadow: "0 4px 15px rgba(0,0,0,0.1)" }}>Upload & Sign</button>
          <button onClick={() => navigate("/documents")}style={{ padding: "10px 24px", borderRadius: "30px", border: "none", background: "rgba(255,255,255,0.25)", color: "white", fontWeight: "500", cursor: "pointer" }}>View Documents</button>
        </div>
      </header>

      {/* WHITE CARD */}
      <div style={{
        maxWidth: "900px",
        margin: "-80px auto 80px",
        background: "white",
        borderRadius: "30px",
        boxShadow: "0 20px 60px rgba(0,0,0,0.12)",
        padding: "60px 80px",
        position: "relative",
        zIndex: 10
      }}>
        <h2 style={{ fontSize: "2rem", color: "#003f7d", margin: "0 0 50px 0", fontWeight: "700" }}>
          Upload & Sign Document
        </h2>

        {/* Next Signer Email */}
        <div style={{ marginBottom: "40px" }}>
          <label style={{ display: "block", marginBottom: "10px", color: "#003f7d", fontWeight: "600" }}>
            Next Signer Email
          </label>
          <input
            type="email"
            placeholder="next signer email"
            value={nextSignerEmail}
            onChange={(e) => setNextSignerEmail(e.target.value)}
            style={{
              width: "100%",
              padding: "18px 20px",
              fontSize: "1.1rem",
              border: "1px solid #e0e0e0",
              borderRadius: "12px",
              outline: "none",
              background: "#fafafa"
            }}
          />
          <p style={{ fontSize: "0.95rem", color: "#666", marginTop: "8px" }}>
            Internal or external user email
          </p>
        </div>

        {/* File Upload */}
        <div style={{ marginBottom: "50px" }}>
          <label style={{ display: "block", marginBottom: "10px", color: "#003f7d", fontWeight: "600" }}>
            Select File (PDF Only)
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <button
              style={{
                padding: "12px 24px",
                background: "#f0f4f8",
                color: "#003f7d",
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "500"
              }}
              onClick={() => document.getElementById("fileInput")?.click()}
            >
              Choose File
            </button>
            <span style={{ color: "#666" }}>
              {selectedFile ? selectedFile.name : "No file chosen"}
            </span>
            <input
              id="fileInput"
              type="file"
              accept="application/pdf"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
          </div>
        </div>

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={loading || !selectedFile || !nextSignerEmail}
          style={{
            width: "100%",
            padding: "20px",
            fontSize: "1.4rem",
            fontWeight: "bold",
            color: "white",
            background: loading ? "#a0a0a0" : "#003f7d",
            border: "none",
            borderRadius: "14px",
            cursor: loading ? "not-allowed" : "pointer",
            boxShadow: "0 8px 25px rgba(0,63,125,0.3)"
          }}
        >
          {loading ? "Uploading & Sending..." : "Upload & Send"}
        </button>
      </div>
    </div>
  );
}